import React, { useEffect, useState } from "react";
import { track } from "../../lib/analytics";

/**
 * Floating "Message Mandar" WhatsApp button.
 *
 * Design decisions:
 * - Hidden on initial load; fades in once the user scrolls past the hero
 *   (~600px) so it never fights the on-load moment.
 * - Sebyx-branded (dark bg + teal accent) — not the generic green blob.
 * - Uses wa.me deep link with a pre-filled message. Works on mobile
 *   (opens app) and desktop (opens WhatsApp Web).
 * - Fires a PostHog event on click for measurement.
 */

const PHONE = "919972554702"; // wa.me format: no + no spaces
const PREFILL =
  "Hi Mandar, I visited sebyx.com and I'd like to know more about your sales training programmes.";
const WA_URL = `https://wa.me/${PHONE}?text=${encodeURIComponent(PREFILL)}`;

export default function WhatsAppFloat() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = () => {
    try {
      track("whatsapp_float_click", { source: "landing_float_button" });
    } catch (_) {
      /* analytics never blocks */
    }
  };

  return (
    <a
      href={WA_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      data-testid="whatsapp-float-btn"
      aria-label="Message Mandar on WhatsApp"
      className={[
        "fixed z-50 bottom-6 right-6 md:bottom-8 md:right-8",
        "group inline-flex items-center gap-2.5",
        "pl-3 pr-4 py-2.5 md:pl-3.5 md:pr-5 md:py-3",
        "bg-[#0A0F1C]/95 backdrop-blur",
        "border border-sebyx-accent",
        "shadow-[0_12px_40px_-12px_rgba(79,185,175,0.45)]",
        "hover:shadow-[0_16px_48px_-8px_rgba(79,185,175,0.6)]",
        "hover:bg-sebyx-accent hover:border-sebyx-accent",
        "transition-[opacity,transform,background-color,box-shadow] duration-500 ease-out",
        "print:hidden",
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-3 pointer-events-none",
      ].join(" ")}
    >
      {/* WhatsApp mark (inline SVG for exact color control) */}
      <span className="relative flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full bg-sebyx-accent group-hover:bg-sebyx-bg transition-colors">
        <svg
          viewBox="0 0 24 24"
          className="w-4 h-4 md:w-[18px] md:h-[18px] text-sebyx-bg group-hover:text-sebyx-accent transition-colors"
          fill="currentColor"
          aria-hidden
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.036 21.5h-.003a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.887 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.304-1.654a11.87 11.87 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 00-3.48-8.413z" />
        </svg>
        {/* subtle pulse ring */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-full border border-sebyx-accent opacity-0 group-hover:opacity-40 group-hover:animate-ping"
        />
      </span>
      <span className="hidden sm:flex flex-col leading-tight">
        <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-muted group-hover:text-sebyx-bg/80 transition-colors">
          WhatsApp
        </span>
        <span className="font-display text-sm font-semibold text-sebyx-text group-hover:text-sebyx-bg transition-colors">
          Message Mandar
        </span>
      </span>
    </a>
  );
}
